// client/src/components/pages/TransactionSearch/index.tsx (converted from .js, ST-00218 lm-status)
//
// Read-only search over the current tickets and the amendment history (the amendments v3 lists),
// with find-in-results, a column picker and an XLSX export. The current tickets carry the
// "Load status" column (load-match ledger label) while the server runs load matching.
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ComponentType,
  type ForwardRefExoticComponent,
  type ReactNode,
  type RefAttributes,
} from 'react';
import { FaSearch, FaFilter } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import type { AxiosResponse } from 'axios';
import api from '../../../services/api';
import FilterControlsJs from '../AmendmentsAllocateTransactions/FilterControls';
import DataTableJs from '../AmendmentsAllocateTransactions/DataTable';
import ColumnSelectorJs from '../AmendmentsAllocateTransactions/ColumnSelector';
import VersionHistoryModalAllocateTransactionsJs from '../AmendmentsAllocateTransactions/VersionHistoryModalAllocateTransactions';
import TransactionDetailsModalJs from '../AmendmentsAllocateTransactions/TransactionDetailsModal';
import ShareTransactionModalJs from '../AmendmentsAllocateTransactions/ShareTransactionModal';
import RegNoTransactionsModalAmendmentsJs from '../AmendmentsAllocateTransactions/RegNoTransactionsModalAmendments';
import CopyableCellJs from '../AmendmentsAllocateTransactions/CopyableCell';
import {
  addLoadStatusToExport,
  useLoadStatusColumn,
} from '../AmendmentsAllocateTransactions/loadStatusColumn';
import FindBarJs from '../../common/FindBar';
import {
  PageHeader,
  SegmentedControl,
  ThemedSelect as ThemedSelectBase,
  type ThemedSelectProps,
} from '../../../types/commonComponents';
import { toApiDateString, formatAppDateTime } from '../../../utils/dateUtils';
import type {
  AmendmentFilters,
  AmendmentTicketRow,
  ApiErrorBody,
  FindMatch,
  FindResponse,
  TicketPage,
} from '../../../types/clientPages/amendments';

type Tab = 'current' | 'history';

interface PaginationState {
  page: number;
  limit: number;
  totalPages: number;
  totalCount: number;
}

interface SortConfig {
  /** Bracketed view column ('[Ticket Timestamp]'); null = the server's default order. */
  sortBy: string | null;
  sortDir: 'ASC' | 'DESC';
  thenBy: string | null;
  thenDir: 'ASC' | 'DESC';
}

/** What the page reads from a rejected request (axios error, or an abort). */
type RequestError = Error & {
  code?: string;
  __CANCEL__?: boolean;
  response?: { status?: number; data?: ApiErrorBody };
};

/** A table cell value (react-table `value`). */
type CellValue = string | number | boolean | null | undefined;

/** The react-table cell props the column renderers read. */
interface TicketCellProps<V = CellValue> {
  value: V;
  row?: { original?: AmendmentTicketRow };
}

/** A react-table v7 column of the ticket tables (DataTable, ColumnSelector). */
interface TicketColumn {
  Header: string;
  accessor: string;
  // Method syntax on purpose: each column's renderer reads its own value type.
  Cell?(props: TicketCellProps): ReactNode;
}

const EMPTY_PAGINATION = { page: 1, totalPages: 1, totalCount: 0 };

// Typed views of the (still JavaScript) components this page renders.
const FilterControls = FilterControlsJs as unknown as ComponentType<{
  onFilterChange: (filters: AmendmentFilters) => void;
  onClear: () => void;
  isLoading: boolean;
  onCancelRequest: () => void;
}>;
const DataTable = DataTableJs as unknown as ComponentType<{
  columns: TicketColumn[];
  data: AmendmentTicketRow[];
  pagination: PaginationState;
  onPageChange: (page: number) => void;
  onViewHistory?: (ticketUID: string) => void;
  onViewTransactionDetails?: (row: AmendmentTicketRow) => void;
  onShareTransaction?: (row: AmendmentTicketRow) => void;
  loading: boolean;
  highlightTicketUID?: string | null;
  readOnly?: boolean;
}>;
const ColumnSelector = ColumnSelectorJs as unknown as ComponentType<{
  allColumns: TicketColumn[];
  visibleColumns: TicketColumn[];
  onVisibleColumnsChange: (columns: TicketColumn[]) => void;
}>;
const VersionHistoryModalAllocateTransactions =
  VersionHistoryModalAllocateTransactionsJs as unknown as ComponentType<{
    isOpen: boolean;
    onClose: () => void;
    ticketUID: string | null;
  }>;
interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactionData: AmendmentTicketRow | null;
}
const TransactionDetailsModal =
  TransactionDetailsModalJs as unknown as ComponentType<TransactionModalProps>;
const ShareTransactionModal =
  ShareTransactionModalJs as unknown as ComponentType<TransactionModalProps>;
const RegNoTransactionsModalAmendments =
  RegNoTransactionsModalAmendmentsJs as unknown as ComponentType<{
    isOpen: boolean;
    onClose: () => void;
    regNo: string | null;
    clickedTicketNo: string | null;
    inheritedFilters: AmendmentFilters;
  }>;
const FindBar = FindBarJs as unknown as ForwardRefExoticComponent<
  {
    value: string;
    onChange: (value: string) => void;
    onSubmit: (term: string) => void;
    onNext: () => void;
    onPrev: () => void;
    onClear: () => void;
    totalMatches: number | null;
    currentIndex: number;
    loading: boolean;
    capped: boolean;
    lastSearchedTerm: string | null;
    errorMessage: string | null;
    placeholder?: string;
  } & RefAttributes<HTMLInputElement>
>;
const CopyableCell = CopyableCellJs as unknown as ComponentType<{
  value: CellValue;
  children?: ReactNode;
  className?: string;
}>;
/** The page-size select takes numeric option values (ThemedSelect matches them by String()). */
const ThemedSelect = ThemedSelectBase as unknown as ComponentType<
  Omit<ThemedSelectProps, 'value'> & { value: string | number }
>;

/** Axios 1.x throws CanceledError on abort; older code only looked for AbortError. */
const isCancel = (err: RequestError | null | undefined): boolean =>
  err?.name === 'AbortError' ||
  err?.name === 'CanceledError' ||
  err?.code === 'ERR_CANCELED' ||
  err?.__CANCEL__ === true;

const TransactionSearch = () => {
  const [activeTab, setActiveTab] = useState<Tab>('current');
  const [currentTickets, setCurrentTickets] = useState<AmendmentTicketRow[]>([]);
  const [historyTickets, setHistoryTickets] = useState<AmendmentTicketRow[]>([]);
  const [resultsPerPage, setResultsPerPage] = useState(25);
  const [currentPagination, setCurrentPagination] = useState<PaginationState>({
    page: 1,
    limit: 25,
    totalPages: 1,
    totalCount: 0,
  });
  const [historyPagination, setHistoryPagination] = useState<PaginationState>({
    page: 1,
    limit: 25,
    totalPages: 1,
    totalCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AmendmentFilters>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    sortBy: '[Ticket Timestamp]',
    sortDir: 'ASC',
    thenBy: null,
    thenDir: 'ASC',
  });
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedTicketUID, setSelectedTicketUID] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [isTransactionDetailsModalOpen, setIsTransactionDetailsModalOpen] = useState(false);
  const [selectedTransactionData, setSelectedTransactionData] = useState<AmendmentTicketRow | null>(
    null
  );
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareTransactionData, setShareTransactionData] = useState<AmendmentTicketRow | null>(null);
  const [isRegNoModalOpen, setIsRegNoModalOpen] = useState(false);
  const [selectedRegNo, setSelectedRegNo] = useState<string | null>(null);
  const [clickedTicketNoForRegNo, setClickedTicketNoForRegNo] = useState<string | null>(null);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // --- Find-in-results state (Current tab only) ---
  const [findTerm, setFindTerm] = useState('');
  const [findMatches, setFindMatches] = useState<FindMatch[] | null>(null); // null = not yet searched
  const [findIndex, setFindIndex] = useState(0);
  const [findLoading, setFindLoading] = useState(false);
  const [findError, setFindError] = useState<string | null>(null);
  const [findCapped, setFindCapped] = useState(false);
  const [lastSearchedTerm, setLastSearchedTerm] = useState<string | null>(null);
  const [highlightTicketUID, setHighlightTicketUID] = useState<string | null>(null);
  const findInputRef = useRef<HTMLInputElement>(null);
  const findAbortRef = useRef<AbortController | null>(null);
  const handlePageChangeRef = useRef<((tab: Tab, newPage: number) => void) | null>(null);

  // --- Find-in-results handlers ---
  const clearFind = useCallback(() => {
    if (findAbortRef.current) {
      try {
        findAbortRef.current.abort();
      } catch (_) {}
      findAbortRef.current = null;
    }
    setFindTerm('');
    setFindMatches(null);
    setFindIndex(0);
    setFindError(null);
    setFindCapped(false);
    setLastSearchedTerm(null);
    setHighlightTicketUID(null);
  }, []);

  const gotoMatch = useCallback(
    (index: number, matches: FindMatch[] | null) => {
      if (!matches || matches.length === 0) return;
      const count = matches.length;
      const wrapped = ((index % count) + count) % count;
      setFindIndex(wrapped);
      const match = matches[wrapped]!;
      setHighlightTicketUID(match.ticketUID);
      if (match.page && match.page !== currentPagination.page) {
        handlePageChangeRef.current?.('current', match.page);
      }
    },
    [currentPagination.page]
  );

  const runFind = useCallback(
    async (rawTerm: string | null | undefined) => {
      const term = (rawTerm || '').trim();
      if (!term) {
        clearFind();
        return;
      }

      if (findAbortRef.current) {
        try {
          findAbortRef.current.abort();
        } catch (_) {}
      }
      findAbortRef.current = new AbortController();

      setFindLoading(true);
      setFindError(null);
      try {
        const params: Record<string, unknown> = {
          searchTerm: term,
          ...filters,
          limit: resultsPerPage,
        };
        if (sortConfig.sortBy) {
          params.sortBy = sortConfig.sortBy;
          params.sortDir = sortConfig.sortDir;
          if (sortConfig.thenBy) {
            params.thenBy = sortConfig.thenBy;
            params.thenDir = sortConfig.thenDir;
          }
        }
        const res: AxiosResponse<FindResponse | undefined> = await api.amendmentsV3.find(params, {
          signal: findAbortRef.current.signal,
        });
        const data: FindResponse = res.data || {};
        const matches = Array.isArray(data.matches) ? data.matches : [];
        setFindMatches(matches);
        setFindCapped(!!data.capped);
        setLastSearchedTerm(term);
        if (matches.length > 0) {
          gotoMatch(0, matches);
        } else {
          setHighlightTicketUID(null);
          setFindIndex(0);
        }
      } catch (caught) {
        const err = caught as RequestError;
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        setFindError(err.response?.data?.message || err.message || 'Find failed');
        setFindMatches([]);
        setLastSearchedTerm(term);
      } finally {
        setFindLoading(false);
      }
    },
    [filters, sortConfig, resultsPerPage, gotoMatch, clearFind]
  );

  const findNext = useCallback(() => {
    if (findMatches && findMatches.length > 0) gotoMatch(findIndex + 1, findMatches);
  }, [findMatches, findIndex, gotoMatch]);

  const findPrev = useCallback(() => {
    if (findMatches && findMatches.length > 0) gotoMatch(findIndex - 1, findMatches);
  }, [findMatches, findIndex, gotoMatch]);

  // Reg No click → transactions modal
  const handleRegNoClick = useCallback((regNo: string, ticketNo?: string | null) => {
    if (!regNo) return;
    setSelectedRegNo(regNo);
    setClickedTicketNoForRegNo(ticketNo || null);
    setIsRegNoModalOpen(true);
  }, []);

  const handleCloseRegNoModal = useCallback(() => {
    setIsRegNoModalOpen(false);
    setSelectedRegNo(null);
    setClickedTicketNoForRegNo(null);
  }, []);

  const regNoCell = useCallback(
    ({ value, row }: TicketCellProps<string | null | undefined>) => {
      if (!value) return <span className="text-slate-400">—</span>;
      const ticketNo = row?.original?.['Ticket No'];
      return (
        <CopyableCell value={value}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRegNoClick(value, ticketNo);
            }}
            title={`View all transactions for ${value}`}
            className="font-mono text-primary-700 hover:text-primary-900 hover:underline underline-offset-2 decoration-primary-400/60 transition-colors"
          >
            {value}
          </button>
        </CopyableCell>
      );
    },
    [handleRegNoClick]
  );

  const copyableTextCell = useCallback(
    ({ value }: TicketCellProps) => <CopyableCell value={value} />,
    []
  );

  // The ticket time as its SAST wall-clock digits (ST-00162: never re-converted).
  const formatTicketTimestamp = useCallback((value: string | number | null | undefined) => {
    if (!value) return '';
    if (typeof value === 'string') {
      if (value.includes('T')) return value.replace('T', ' ').substring(0, 19);
      return value;
    }
    return new Date(value).toISOString().replace('T', ' ').substring(0, 19);
  }, []);

  const ticketTimestampCell = useCallback(
    ({ value }: TicketCellProps<string | number | null | undefined>) => {
      const formatted = formatTicketTimestamp(value);
      if (!formatted) return '';
      return <CopyableCell value={formatted} />;
    },
    [formatTicketTimestamp]
  );

  // --- Column Definitions ---
  const allCurrentColumns = useMemo<TicketColumn[]>(
    () => [
      { Header: 'Ticket No', accessor: 'Ticket No', Cell: copyableTextCell },
      { Header: 'Delivery No', accessor: 'Delivery No', Cell: copyableTextCell },
      { Header: 'Order No', accessor: 'Order No', Cell: copyableTextCell },
      { Header: 'Timestamp', accessor: 'Ticket Timestamp', Cell: ticketTimestampCell },
      { Header: 'Type', accessor: 'Ticket Type' },
      { Header: 'Dispatch Loc', accessor: 'Dispatch Location', Cell: copyableTextCell },
      { Header: 'Dispatch Org', accessor: 'Dispatch Organisation' },
      { Header: 'Receipt Loc', accessor: 'Receipt Location', Cell: copyableTextCell },
      { Header: 'Receipt Org', accessor: 'Receipt Organisation' },
      { Header: 'Reg No', accessor: 'Reg No', Cell: regNoCell },
      { Header: 'Ref No', accessor: 'Ref No' },
      { Header: 'Product', accessor: 'Product' },
      { Header: 'First Wgt', accessor: 'First Weight' },
      { Header: 'Second Wgt', accessor: 'Second Weight' },
      { Header: 'Nett Wgt', accessor: 'Nett Weight' },
      { Header: 'Version', accessor: 'Version' },
      { Header: 'TicketUID', accessor: 'TicketUID' },
    ],
    [regNoCell, copyableTextCell, ticketTimestampCell]
  );

  const allHistoryColumns = useMemo<TicketColumn[]>(
    () => [
      { Header: 'Version', accessor: 'Version' },
      { Header: 'Ticket No', accessor: 'Ticket No', Cell: copyableTextCell },
      { Header: 'Delivery No', accessor: 'Delivery No', Cell: copyableTextCell },
      { Header: 'Order No', accessor: 'Order No', Cell: copyableTextCell },
      {
        Header: 'Amended At',
        accessor: 'AmendmentTimestamp',
        Cell: ({ value }: TicketCellProps<string | null | undefined>) => formatAppDateTime(value),
      },
      { Header: 'Amended By', accessor: 'AmendmentUser' },
      { Header: 'Reason for Next Version', accessor: 'AmendmentNotes' },
      { Header: 'Timestamp', accessor: 'Ticket Timestamp', Cell: ticketTimestampCell },
      { Header: 'Type', accessor: 'Ticket Type' },
      { Header: 'Dispatch Loc', accessor: 'Dispatch Location', Cell: copyableTextCell },
      { Header: 'Dispatch Org', accessor: 'Dispatch Organisation' },
      { Header: 'Receipt Loc', accessor: 'Receipt Location', Cell: copyableTextCell },
      { Header: 'Receipt Org', accessor: 'Receipt Organisation' },
      { Header: 'Reg No', accessor: 'Reg No', Cell: regNoCell },
      { Header: 'Ref No', accessor: 'Ref No' },
      { Header: 'Product', accessor: 'Product' },
      { Header: 'First Wgt', accessor: 'First Weight' },
      { Header: 'Second Wgt', accessor: 'Second Weight' },
      { Header: 'Gross Wgt', accessor: 'Gross Weight' },
      { Header: 'Tare Wgt', accessor: 'Tare Weight' },
      { Header: 'Nett Wgt', accessor: 'Nett Weight' },
      { Header: 'TicketUID', accessor: 'TicketUID' },
    ],
    [regNoCell, copyableTextCell, ticketTimestampCell]
  );

  const defaultCurrentColumns = useMemo(
    () =>
      allCurrentColumns.filter((col) =>
        [
          'Ticket No',
          'Delivery No',
          'Order No',
          'Ticket Timestamp',
          'Ticket Type',
          'Dispatch Location',
          'Receipt Location',
          'Reg No',
          'Ref No',
          'Nett Weight',
          'Version',
        ].includes(col.accessor)
      ),
    [allCurrentColumns]
  );

  const defaultHistoryColumns = useMemo(
    () =>
      allHistoryColumns.filter((col) =>
        [
          'Version',
          'Ticket No',
          'Delivery No',
          'Order No',
          'AmendmentTimestamp',
          'AmendmentUser',
          'Ticket Timestamp',
          'Ticket Type',
          'Dispatch Location',
          'Receipt Location',
          'Reg No',
          'Ref No',
          'Nett Weight',
          'AmendmentNotes',
        ].includes(col.accessor)
      ),
    [allHistoryColumns]
  );

  const [visibleCurrentColumns, setVisibleCurrentColumns] = useState(defaultCurrentColumns);
  const [visibleHistoryColumns, setVisibleHistoryColumns] = useState(defaultHistoryColumns);

  const handleVisibleCurrentColumnsChange = useCallback((newVisibleColumns: TicketColumn[]) => {
    setVisibleCurrentColumns(newVisibleColumns);
  }, []);

  const handleVisibleHistoryColumnsChange = useCallback((newVisibleColumns: TicketColumn[]) => {
    setVisibleHistoryColumns(newVisibleColumns);
  }, []);

  // ST-00218 lm-status: the "Load status" column of the current tickets (after the ticket type,
  // visible by default, in the column picker) while the server runs load matching. The history
  // tab lists superseded versions of a ticket, so it has none.
  const loadStatus = useLoadStatusColumn(
    currentTickets,
    allCurrentColumns,
    visibleCurrentColumns,
    handleVisibleCurrentColumnsChange
  );

  // --- Data Fetching ---
  const fetchData = useCallback(
    async (
      tab: Tab,
      page = 1,
      currentFilters: AmendmentFilters = filters,
      currentSortConfig: SortConfig = sortConfig
    ) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      setLoading(true);
      setError(null);
      const isCurrentTab = tab === 'current';

      const paginationParams = {
        page,
        limit: resultsPerPage,
        ...currentFilters,
        ...(currentSortConfig.sortBy && {
          sortBy: currentSortConfig.sortBy,
          sortDir: currentSortConfig.sortDir,
          ...(currentSortConfig.thenBy && {
            thenBy: currentSortConfig.thenBy,
            thenDir: currentSortConfig.thenDir,
          }),
        }),
      };

      try {
        const fetchFn = isCurrentTab
          ? api.amendmentsV3.getLatestTickets
          : api.amendmentsV3.getAmendedTickets;
        const response: AxiosResponse<TicketPage> = await fetchFn(paginationParams, { signal });

        if (isCurrentTab) {
          setCurrentTickets(response.data.data || []);
          setCurrentPagination({
            ...(response.data.pagination || EMPTY_PAGINATION),
            limit: resultsPerPage,
          });
        } else {
          setHistoryTickets(response.data.data || []);
          setHistoryPagination({
            ...(response.data.pagination || EMPTY_PAGINATION),
            limit: resultsPerPage,
          });
        }
      } catch (caught) {
        if (!isCancel(caught as RequestError | null | undefined)) {
          setError(`Failed to fetch ${tab} tickets. Please check connection or filters.`);
        }
      } finally {
        setLoading(false);
        if (signal === abortControllerRef.current?.signal) {
          abortControllerRef.current = null;
        }
      }
    },
    [filters, resultsPerPage, sortConfig]
  );

  useEffect(() => {
    if (hasAppliedFilters) {
      fetchData(activeTab, 1, filters, sortConfig);
    } else {
      setCurrentTickets([]);
      setHistoryTickets([]);
      setCurrentPagination({ page: 1, limit: resultsPerPage, totalPages: 1, totalCount: 0 });
      setHistoryPagination({ page: 1, limit: resultsPerPage, totalPages: 1, totalCount: 0 });
    }
  }, [activeTab, filters, sortConfig, resultsPerPage, hasAppliedFilters, fetchData]);

  const handlePageChange = (tab: Tab, newPage: number) => {
    if (tab === 'current') {
      setCurrentPagination((prev) => ({ ...prev, page: newPage }));
      fetchData('current', newPage, filters, sortConfig);
    } else {
      setHistoryPagination((prev) => ({ ...prev, page: newPage }));
      fetchData('history', newPage, filters, sortConfig);
    }
  };
  handlePageChangeRef.current = handlePageChange;

  useEffect(() => {
    clearFind();
  }, [filters, sortConfig, resultsPerPage, clearFind]);

  // Ctrl/Cmd+F override — only on the Current tab
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (activeTab !== 'current') return;
      const isFind =
        (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.key === 'f' || e.key === 'F');
      if (!isFind) return;
      e.preventDefault();
      const el = findInputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeTab]);

  const handleFilterChange = (newFilters: AmendmentFilters) => {
    setFilters(newFilters);
    if (!hasAppliedFilters) setHasAppliedFilters(true);
  };

  const handleClearFilters = () => {
    setHasAppliedFilters(false);
    setFilters({});
    setCurrentTickets([]);
    setHistoryTickets([]);
    setCurrentPagination({ page: 1, limit: resultsPerPage, totalPages: 1, totalCount: 0 });
    setHistoryPagination({ page: 1, limit: resultsPerPage, totalPages: 1, totalCount: 0 });
    setError(null);
    setExportError(null);
  };

  // ThemedSelect hands back the option's own value (a number here); parseInt reads it as
  // String(value), so the explicit String() is the same conversion.
  const handleResultsPerPageChange = (event: { target: { value: string | number } }) => {
    const newLimit = parseInt(String(event.target.value), 10);
    setResultsPerPage(newLimit);
    setCurrentPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
    setHistoryPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  // --- Sorting ---
  const handleSortByRegThenTimestamp = () => {
    const isCurrentSort =
      sortConfig.sortBy === '[Reg No]' && sortConfig.thenBy === '[Ticket Timestamp]';
    const nextSortDir = isCurrentSort ? (sortConfig.sortDir === 'ASC' ? 'DESC' : 'ASC') : 'ASC';
    setSortConfig({
      sortBy: '[Reg No]',
      sortDir: nextSortDir,
      thenBy: '[Ticket Timestamp]',
      thenDir: 'ASC',
    });
  };

  const handleSortByTimestamp = () => {
    const isCurrentSort = sortConfig.sortBy === '[Ticket Timestamp]' && !sortConfig.thenBy;
    const nextSortDir = isCurrentSort ? (sortConfig.sortDir === 'ASC' ? 'DESC' : 'ASC') : 'DESC';
    setSortConfig({
      sortBy: '[Ticket Timestamp]',
      sortDir: nextSortDir,
      thenBy: null,
      thenDir: 'ASC',
    });
  };

  const handleClearSort = () => {
    setSortConfig({ sortBy: null, sortDir: 'DESC', thenBy: null, thenDir: 'ASC' });
  };

  // --- Modal Handling ---
  const handleOpenHistoryModal = (ticketUID: string) => {
    setSelectedTicketUID(ticketUID);
    setIsHistoryModalOpen(true);
  };

  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setSelectedTicketUID(null);
  };

  const handleViewTransactionDetails = (transactionData: AmendmentTicketRow) => {
    setSelectedTransactionData(transactionData);
    setIsTransactionDetailsModalOpen(true);
  };

  const handleCloseTransactionDetailsModal = () => {
    setIsTransactionDetailsModalOpen(false);
    setSelectedTransactionData(null);
  };

  const handleShareTransaction = (transactionData: AmendmentTicketRow) => {
    setShareTransactionData(transactionData);
    setIsShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
    setShareTransactionData(null);
  };

  // --- Export Logic ---
  const handleExport = async (format: string, tabType: Tab = 'history') => {
    setIsExporting(true);
    setExportError(null);

    try {
      let response: AxiosResponse<TicketPage>;
      let sheetName: string;
      let filePrefix: string;

      if (tabType === 'current') {
        response = await api.amendmentsV3.getLatestTicketsForExport(filters);
        sheetName = 'Current Tickets';
        filePrefix = 'transaction_search_current';
      } else {
        response = await api.amendmentsV3.getAmendedTicketsForExport(filters);
        sheetName = 'Amendment History';
        filePrefix = 'transaction_search_history';
      }

      const dataToExport = response.data.data || [];

      if (dataToExport.length === 0) {
        setExportError(`No ${tabType} data found matching the current filters to export.`);
        setIsExporting(false);
        return;
      }

      // ST-00218 lm-status: current tickets get a "Load status" text column when load matching
      // is on (labels looked up for every exported row, not only the ones on screen).
      const sheetRows =
        tabType === 'current' ? (await addLoadStatusToExport(dataToExport)).rows : dataToExport;
      const worksheet = XLSX.utils.json_to_sheet(sheetRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      const dateStr = toApiDateString(new Date());
      const fileName = `${filePrefix}_${dateStr}.${format}`;
      XLSX.writeFile(workbook, fileName);
    } catch (caught) {
      const err = caught as RequestError;
      setExportError(err.response?.data?.message || err.message || 'Failed to export data.');
    } finally {
      setIsExporting(false);
    }
  };

  const [isFilterOpen, setIsFilterOpen] = useState(true);

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaSearch}
        title="Transaction Search"
        subtitle="Search and view transactions"
        className="mb-3"
        toolbarAlign="start"
        toolbar={
          <SegmentedControl
            ariaLabel="Transaction Search view"
            value={activeTab}
            onChange={setActiveTab}
            options={[
              {
                value: 'current',
                label: 'Current Tickets',
                shortLabel: 'Current',
                badge:
                  hasAppliedFilters && currentPagination.totalCount > 0
                    ? currentPagination.totalCount.toLocaleString()
                    : null,
              },
              {
                value: 'history',
                label: 'Amendment History',
                shortLabel: 'History',
                badge:
                  hasAppliedFilters && historyPagination.totalCount > 0
                    ? historyPagination.totalCount.toLocaleString()
                    : null,
              },
            ]}
          />
        }
        actions={
          <button
            type="button"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            aria-pressed={isFilterOpen}
            aria-label={isFilterOpen ? 'Hide filters' : 'Show filters'}
            title={isFilterOpen ? 'Hide filters' : 'Show filters'}
            className={`inline-flex items-center justify-center gap-1.5 h-10 min-w-10 px-2.5 text-sm font-medium rounded-lg border transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              isFilterOpen
                ? 'bg-primary-500/10 text-primary-700 border-primary-500'
                : 'bg-surface text-slate-600 border-slate-300 hover:border-slate-400'
            }`}
          >
            <FaFilter className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="page-header__label">
              {isFilterOpen ? 'Hide Filters' : 'Show Filters'}
            </span>
          </button>
        }
      />

      {/* Collapsible Filter Controls */}
      {isFilterOpen && (
        <div className="mb-3">
          <FilterControls
            onFilterChange={handleFilterChange}
            onClear={handleClearFilters}
            isLoading={loading}
            onCancelRequest={() => {
              if (abortControllerRef.current) {
                abortControllerRef.current.abort();
              }
            }}
          />
        </div>
      )}

      {/* Data Area */}
      <div className="bg-surface border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        {/* Integrated Toolbar: Sort + Results per page + Export + Columns */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-200 bg-slate-50/50">
          {/* Left: Sort controls */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              Sort
            </span>
            <button
              onClick={handleSortByTimestamp}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border transition-all ${
                sortConfig.sortBy === '[Ticket Timestamp]' && !sortConfig.thenBy
                  ? 'bg-primary-500/10 text-primary-700 border-primary-500'
                  : 'bg-surface text-slate-600 border-slate-300 hover:border-slate-400'
              }`}
            >
              Timestamp
              {sortConfig.sortBy === '[Ticket Timestamp]' && !sortConfig.thenBy && (
                <span className="text-[10px]">{sortConfig.sortDir === 'ASC' ? '↑' : '↓'}</span>
              )}
            </button>
            <button
              onClick={handleSortByRegThenTimestamp}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border transition-all ${
                sortConfig.sortBy === '[Reg No]' && sortConfig.thenBy === '[Ticket Timestamp]'
                  ? 'bg-primary-500/10 text-primary-700 border-primary-500'
                  : 'bg-surface text-slate-600 border-slate-300 hover:border-slate-400'
              }`}
            >
              Reg + Time
              {sortConfig.sortBy === '[Reg No]' && sortConfig.thenBy === '[Ticket Timestamp]' && (
                <span className="text-[10px]">{sortConfig.sortDir === 'ASC' ? '↑' : '↓'}</span>
              )}
            </button>
            {sortConfig.sortBy && (
              <button
                onClick={handleClearSort}
                className="inline-flex items-center px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                title="Clear Sorting"
              >
                <svg
                  className="w-3 h-3 mr-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear
              </button>
            )}

            <span className="text-slate-300 mx-1">|</span>

            {/* Results Per Page */}
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              Show
            </span>
            <ThemedSelect
              id="resultsPerPage"
              value={resultsPerPage}
              onChange={handleResultsPerPageChange}
              className="block w-auto pl-2 pr-7 py-1 text-xs border border-slate-300 bg-surface rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={150}>150</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={10000}>10,000</option>
            </ThemedSelect>
          </div>

          {/* Right: Export + Column selector */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleExport('xlsx', activeTab === 'current' ? 'current' : 'history')}
              disabled={isExporting || loading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 disabled:opacity-50 transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {isExporting ? 'Exporting...' : 'Export XLSX'}
            </button>
            {activeTab === 'current' && (
              <ColumnSelector
                allColumns={loadStatus.allColumns}
                visibleColumns={loadStatus.visibleColumns}
                onVisibleColumnsChange={loadStatus.onVisibleColumnsChange}
              />
            )}
            {activeTab === 'history' && (
              <ColumnSelector
                allColumns={allHistoryColumns}
                visibleColumns={visibleHistoryColumns}
                onVisibleColumnsChange={handleVisibleHistoryColumnsChange}
              />
            )}
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="mx-4 mt-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </div>
        )}
        {exportError && (
          <div className="mx-4 mt-3 p-2.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-md flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            Export: {exportError}
          </div>
        )}

        {/* Table Content */}
        <div className="p-0">
          {activeTab === 'current' && (
            <>
              <FindBar
                ref={findInputRef}
                value={findTerm}
                onChange={setFindTerm}
                onSubmit={runFind}
                onNext={findNext}
                onPrev={findPrev}
                onClear={clearFind}
                totalMatches={findMatches ? findMatches.length : null}
                currentIndex={findIndex}
                loading={findLoading}
                capped={findCapped}
                lastSearchedTerm={lastSearchedTerm}
                errorMessage={findError}
                placeholder="Find in results  —  Ticket No, Delivery No, Order No, Ref No, Reg No"
              />
              <DataTable
                columns={loadStatus.visibleColumns}
                data={currentTickets}
                pagination={currentPagination}
                onPageChange={(newPage) => handlePageChange('current', newPage)}
                onViewHistory={handleOpenHistoryModal}
                onViewTransactionDetails={handleViewTransactionDetails}
                onShareTransaction={handleShareTransaction}
                loading={loading}
                highlightTicketUID={highlightTicketUID}
                readOnly
              />
            </>
          )}

          {activeTab === 'history' && (
            <DataTable
              columns={visibleHistoryColumns}
              data={historyTickets}
              pagination={historyPagination}
              onPageChange={(newPage) => handlePageChange('history', newPage)}
              loading={loading}
              readOnly
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <VersionHistoryModalAllocateTransactions
        isOpen={isHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        ticketUID={selectedTicketUID}
      />
      <TransactionDetailsModal
        isOpen={isTransactionDetailsModalOpen}
        onClose={handleCloseTransactionDetailsModal}
        transactionData={selectedTransactionData}
      />
      <ShareTransactionModal
        isOpen={isShareModalOpen}
        onClose={handleCloseShareModal}
        transactionData={shareTransactionData}
      />
      <RegNoTransactionsModalAmendments
        isOpen={isRegNoModalOpen}
        onClose={handleCloseRegNoModal}
        regNo={selectedRegNo}
        clickedTicketNo={clickedTicketNoForRegNo}
        inheritedFilters={filters}
      />
    </div>
  );
};

export default TransactionSearch;
